import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Download, Loader2, CheckCircle2, ArrowRight, Eraser, ListChecks, Database, AlertTriangle } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";

export default function CsvCenter() {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [seedOnly, setSeedOnly] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [checking, setChecking] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportInfo, setExportInfo] = useState(null);
  const inputRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.endsWith(".csv")) setFile(f);
    else toast.error("Please drop a .csv file");
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(seedOnly ? "/csv/import?seed_only=true" : "/csv/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(seedOnly ? `Seeded ${data.seeded} TCGplayer SKUs` : `Imported: ${data.created} new, ${data.updated} updated`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["sets"] });
      if (coverage) checkCoverage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/csv/export", { responseType: "blob" });
      const hasInfo = res.headers["x-exported-rows"] !== undefined;
      const exported = Number(res.headers["x-exported-rows"] || 0);
      const omitted = Number(res.headers["x-omitted-rows"] || 0);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "tcgplayer_inventory.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setExportInfo(hasInfo ? { exported, omitted } : null);
      if (hasInfo && omitted > 0) {
        toast.warning(`${omitted} card${omitted !== 1 ? "s" : ""} skipped — no TCGplayer SKU yet`);
        checkCoverage();
      } else {
        toast.success(hasInfo ? `Exported ${exported} card${exported !== 1 ? "s" : ""}` : "CSV downloaded");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const checkCoverage = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/csv/seed-status");
      setCoverage(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear your ENTIRE inventory? This permanently deletes every card and cannot be undone.")) return;
    if (!window.confirm("Are you absolutely sure? All cards will be gone.")) return;
    try {
      const { data } = await api.delete("/cards");
      toast.success(`Cleared ${data.deleted} cards`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["sets"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">TCGPlayer CSV Center</h1>
        <p className="text-sm text-slate-400 mt-1">Import your TCGPlayer staged inventory CSV, or export a fresh file to re-upload to TCGPlayer.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import */}
        <div className="rounded-2xl border p-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Upload className="h-5 w-5 text-cyan-400" />
            <h2 className="font-heading text-lg font-bold text-slate-100">Import from TCGPlayer</h2>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="csv-drag-drop-zone"
            className={`rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center py-12 px-4 text-center transition-colors ${dragOver ? "border-amber-500 bg-amber-500/5" : ""}`}
            style={{ borderColor: dragOver ? undefined : "var(--pk-border)" }}
          >
            <FileSpreadsheet className="h-10 w-10 text-slate-600 mb-3" />
            {file ? (
              <div className="text-sm text-slate-200 font-medium">{file.name}</div>
            ) : (
              <>
                <div className="text-sm text-slate-300 font-medium">Drop CSV here or click to browse</div>
                <div className="text-xs text-slate-500 mt-1">Accepts TCGPlayer pricing/quantity export</div>
              </>
            )}
            <input ref={inputRef} type="file" accept=".csv" hidden data-testid="csv-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>

          <button onClick={doImport} disabled={!file || importing} data-testid="csv-import-execute-button"
            className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-50">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {seedOnly ? "Seed SKUs" : "Import Inventory"}
          </button>

          <label className="mt-3 flex items-start gap-2 text-xs text-slate-400 cursor-pointer" data-testid="csv-seed-only-label">
            <input type="checkbox" checked={seedOnly} onChange={(e) => setSeedOnly(e.target.checked)} data-testid="csv-seed-only-toggle" className="mt-0.5 accent-amber-500" />
            <span><span className="text-slate-300 font-medium">Seed SKUs only</span> — learn the TCGplayer Ids from this file without adding its cards to your inventory. Use this for big past-pricing exports so app-added cards in those sets become uploadable.</span>
          </label>

          {result && (
            <div className="mt-4 rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="csv-map-preview-table">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-3"><CheckCircle2 className="h-4 w-4" /> Import complete</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Stat label="Created" value={result.created} color="text-emerald-400" />
                <Stat label="Updated" value={result.updated} color="text-cyan-400" />
                <Stat label="Skipped" value={result.skipped} color="text-slate-400" />
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-slate-500 leading-relaxed">
            Recognized columns: <span className="text-slate-400 font-mono-num">Product Name, Set Name, Number, Rarity, Condition, TCG Marketplace Price, Total Quantity, TCGplayer Id</span>. Rows are matched by TCGplayer Id (SKU) when present.
          </div>

          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--pk-border)" }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-rose-300">Clear entire inventory</div>
                <div className="text-xs text-slate-500 mt-0.5">Wipe every card to start fresh before a new import.</div>
              </div>
              <button onClick={clearAll} data-testid="csv-clear-inventory-button"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-800/60 text-sm text-rose-400 hover:bg-rose-500/10 shrink-0">
                <Eraser className="h-4 w-4" /> Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Export */}
        <div className="rounded-2xl border p-6 flex flex-col" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Download className="h-5 w-5 text-emerald-400" />
            <h2 className="font-heading text-lg font-bold text-slate-100">Export for TCGPlayer</h2>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            Download your current inventory as a TCGPlayer-formatted CSV. Quantities reflect every sale synced from Shopify and your manual edits, so re-uploading keeps TCGPlayer in step.
          </p>

          <div className="flex-1 flex items-center justify-center my-6">
            <div className="flex items-center gap-4 text-slate-500">
              <div className="flex flex-col items-center gap-1"><div className="h-12 w-12 rounded-xl border flex items-center justify-center" style={{ borderColor: "var(--pk-border)", background: "var(--pk-card)" }}><FileSpreadsheet className="h-5 w-5 text-amber-400" /></div><span className="text-[10px]">Your Vault</span></div>
              <ArrowRight className="h-4 w-4" />
              <div className="flex flex-col items-center gap-1"><div className="h-12 w-12 rounded-xl border flex items-center justify-center bg-blue-950/60 border-blue-800/50"><span className="text-[10px] font-bold text-blue-400">TCG</span></div><span className="text-[10px]">TCGPlayer</span></div>
            </div>
          </div>

          <button onClick={exportCsv} disabled={exporting} data-testid="csv-export-download-button"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold text-slate-100 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)", background: "var(--pk-card)" }}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download CSV
          </button>

          {exportInfo && (
            <div data-testid="csv-export-banner" className={`mt-4 rounded-xl border p-4 text-sm ${exportInfo.omitted > 0 ? "border-amber-500/50 bg-amber-500/5" : "border-emerald-700/50 bg-emerald-500/5"}`}>
              {exportInfo.omitted > 0 ? (
                <>
                  <div className="flex items-center gap-2 text-amber-400 font-medium"><AlertTriangle className="h-4 w-4" /> {exportInfo.exported} exported · {exportInfo.omitted} skipped</div>
                  <p className="text-slate-400 mt-1.5 leading-relaxed">The skipped cards have no TCGplayer SKU yet, so they can't be mass-updated. The sets that need seeding are listed under <span className="text-slate-300 font-medium">TCGplayer Upload Coverage</span> below — import a TCGplayer "Export Filtered CSV" for them with <span className="text-slate-300">Seed SKUs only</span> ticked, then re-export.</p>
                </>
              ) : (
                <div className="flex items-center gap-2 text-emerald-400 font-medium"><CheckCircle2 className="h-4 w-4" /> {exportInfo.exported} card{exportInfo.exported !== 1 ? "s" : ""} exported — all uploadable to TCGplayer.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload coverage — which sets still need SKUs */}
      <div className="mt-6 rounded-2xl border p-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="csv-coverage-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-violet-400" />
            <h2 className="font-heading text-lg font-bold text-slate-100">TCGplayer Upload Coverage</h2>
          </div>
          <button onClick={checkCoverage} disabled={checking} data-testid="csv-check-coverage-button"
            className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold text-violet-300 border-violet-500/50 hover:bg-violet-500/10 disabled:opacity-50">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} Check Coverage
          </button>
        </div>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Cards added in the app can only be uploaded to TCGplayer once we know their SKU. We learn SKUs from every TCGplayer CSV you import. This shows which of your inventory sets still need a one-time TCGplayer <span className="text-slate-300">"Export Filtered CSV"</span> import (seed SKUs only).
        </p>

        {coverage && (
          <div className="mt-4" data-testid="csv-coverage-result">
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <Stat label="SKUs Known" value={coverage.sku_map_size} color="text-violet-400" />
              <Stat label="Cards Ready" value={coverage.covered_count} color="text-emerald-400" />
              <Stat label="Need SKUs" value={coverage.missing_count} color={coverage.missing_count ? "text-amber-400" : "text-slate-400"} />
            </div>
            {coverage.missing_sets?.length ? (
              <div className="rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-3"><AlertTriangle className="h-4 w-4" /> Import a TCGplayer Export Filtered CSV for these sets:</div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {coverage.missing_sets.map((s) => (
                    <div key={s.set_name} className="flex items-center justify-between text-sm py-1 border-b" style={{ borderColor: "var(--pk-border-subtle)" }} data-testid="csv-missing-set-row">
                      <span className="text-slate-200 truncate pr-3">{s.set_name}</span>
                      <span className="font-mono-num text-amber-400 shrink-0">{s.count} card{s.count !== 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border p-4 flex items-center gap-2 text-emerald-400 text-sm" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="csv-coverage-all-good">
                <CheckCircle2 className="h-4 w-4" /> Every inventory card has a known TCGplayer SKU — your export is fully uploadable.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className={`text-2xl font-bold font-mono-num ${color}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
